// Modified from https://github.com/chakra-ui/zag
// MIT License
// Changes from Qualcomm Technologies, Inc. are provided under the following license:
// Copyright (c) Qualcomm Technologies, Inc. and/or its subsidiaries.
// SPDX-License-Identifier: BSD-3-Clause-Clear

import {queryAll} from "@qualcomm-ui/dom/query"
import type {
  ScopeDomElements,
  ScopeDomIds,
  ScopeWithIds,
} from "@qualcomm-ui/utils/machine"

import type {RadioElementIds, RadioSchema} from "../radio.types.js"

type Scope = ScopeWithIds<RadioSchema>

export const domIds: ScopeDomIds<RadioElementIds, Scope> = {
  errorText: (scope) => scope.ids.get("errorText"),
  hint: (scope) => scope.ids.get("hint"),
  item: (scope, itemKey) => scope.ids.collection("item").get(itemKey),
  itemHiddenInput: (scope, itemKey) =>
    scope.ids.collection("itemHiddenInput").get(itemKey),
  itemHint: (scope, itemKey) => scope.ids.collection("itemHint").get(itemKey),
  itemLabel: (scope, itemKey) => scope.ids.collection("itemLabel").get(itemKey),
  label: (scope) => scope.ids.get("label"),
  root: (scope) => scope.ids.get("root"),
}

export const domEls: ScopeDomElements<RadioElementIds, Scope> = {
  errorText: (scope) => scope.getById(domIds.errorText(scope)),
  hint: (scope) => scope.getById(domIds.hint(scope)),
  item: (scope, itemKey) => scope.getById(domIds.item(scope, itemKey)!),
  itemHiddenInput: (scope, itemKey) =>
    scope.getById(domIds.itemHiddenInput(scope, itemKey)!),
  itemHint: (scope, itemKey) => scope.getById(domIds.itemHint(scope, itemKey)!),
  itemLabel: (scope, itemKey) =>
    scope.getById(domIds.itemLabel(scope, itemKey)!),
  label: (scope) => scope.getById(domIds.label(scope)),
  root: (scope) => scope.getById(domIds.root(scope)),
}

export function getInputEls(ctx: Scope): HTMLInputElement[] {
  const ownerId = CSS.escape(domIds.root(ctx))
  const selector = `input[type=radio][data-ownedby='${ownerId}']:not([disabled])`
  return queryAll<HTMLInputElement>(domEls.root(ctx), selector)
}

export function getFirstEnabledInputEl(
  ctx: Scope,
): HTMLInputElement | null | undefined {
  return domEls
    .root(ctx)
    ?.querySelector<HTMLInputElement>("input:not(:disabled)")
}

export function getFirstEnabledAndCheckedInputEl(
  ctx: Scope,
): HTMLInputElement | null | undefined {
  return domEls
    .root(ctx)
    ?.querySelector<HTMLInputElement>("input:not(:disabled):checked")
}
